import { z } from 'zod';
/**
 * Payload shape for `GET /api/dashboard/charger-utilization`.
 *
 * Per-charger utilization over a 7-day window. Utilization is
 * defined as session-based: sum(ended_at - started_at) for
 * valid completed sessions / total window time. A charger
 * sitting idle but pinging heartbeats is NOT utilized — that
 * would be the "activity" measure on /dashboard/charger-uptime.
 *
 * Valid sessions for this purpose
 * ───────────────────────────────
 *   - status in COMPLETED_STATUSES (completed / stopped_by_user
 *     / stopped_by_system)
 *   - energy_kwh > 0 (drops the cancelled_zero_cost
 *     plug-and-unplug rows)
 *
 * Sort: utilization_percent desc — most-utilized first. Helpful
 * for capacity planning ("which chargers are saturated?") and
 * the inverse ("which are under-utilized?" by scrolling to the
 * bottom or filtering on color).
 *
 * Window: 7 days fixed for v1. A 24h/7d/30d toggle is plausible
 * follow-up work if Building Admins ask.
 */
export const ChargerUtilizationRowSchema = z.object({
    charger_id: z.string(),
    charger_identity: z.string(),
    location_name: z.string().nullable(),
    /** 0-100. (sum of valid session durations / 7 days) * 100. */
    utilization_percent: z.number().min(0).max(100),
    /** Number of valid sessions contributing to the total. */
    session_count: z.number().int().nonnegative(),
    /** Total active minutes across the 7-day window — useful in
     *  the row tooltip / detail surface for operators who want
     *  the absolute number behind the percent. */
    total_active_minutes: z.number().nonnegative(),
});
export const ChargerUtilizationResponseSchema = z.object({
    rows: z.array(ChargerUtilizationRowSchema),
    /** Window length in hours. v1 always emits 168 (7 days). The
     *  field is here so a future window-toggle wouldn't need a
     *  schema bump. */
    window_hours: z.number().int().positive(),
    is_scoped: z.boolean(),
    /** Which sessions the rows count: the caller's session scope, or — for
     *  the fleet tier — every session on the chargers the fleet owns,
     *  whoever paid (hardware use). Optional for older payloads. */
    session_basis: z.enum(['scope', 'owned_chargers']).optional(),
    generated_at: z.string(),
});
//# sourceMappingURL=dashboard-charger-utilization.js.map